import React, { useEffect, useState } from 'react';
import {
  Box,
  Heading,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Spinner,
  Center,
  useToast,
  HStack,
  Text,
  Card,
  CardBody,
  CardHeader,
} from '@chakra-ui/react';
import { FiMail, FiBriefcase, FiUsers, FiTrendingUp } from 'react-icons/fi';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  getCRMMetrics,
  CRMMetrics,
} from '../services/crmService';
import FleetInquiriesTab from './CRM/FleetInquiriesTab';
import JobApplicationsTab from './CRM/JobApplicationsTab';
import NewsletterSubscribersTab from './CRM/NewsletterSubscribersTab';

// Cast icons as React.ElementType to fix TypeScript issues
const FiMailIcon = FiMail as React.ElementType;
const FiBriefcaseIcon = FiBriefcase as React.ElementType;
const FiUsersIcon = FiUsers as React.ElementType;
const FiTrendingUpIcon = FiTrendingUp as React.ElementType;

const CRMManagement: React.FC = () => {
  const [metrics, setMetrics] = useState<CRMMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  useEffect(() => {
    fetchMetrics();
  }, []);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      const data = await getCRMMetrics();
      setMetrics(data);
    } catch (error) {
      console.error('Error fetching CRM metrics:', error);
      toast({
        title: 'Error loading CRM data',
        description: 'Failed to fetch CRM metrics',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading || !metrics) {
    return (
      <Center h="400px">
        <Spinner size="xl" color="blue.500" />
      </Center>
    );
  }

  return (
    <Box>
      <Heading
        size={{ base: 'md', md: 'lg' }}
        mb={6}
        pl={{ base: '60px', lg: 0 }}
        pr={{ base: 4, lg: 0 }}
      >
        CRM Management
      </Heading>

      {/* Key Metrics */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} mb={6}>
        <Stat p={4} bg="white" borderRadius="lg" boxShadow="sm">
          <StatLabel>Fleet Inquiries</StatLabel>
          <StatNumber>{metrics.fleetInquiries.total}</StatNumber>
          <StatHelpText>
            {metrics.fleetInquiries.byStatus['new'] || 0} new
          </StatHelpText>
        </Stat>

        <Stat p={4} bg="white" borderRadius="lg" boxShadow="sm">
          <StatLabel>Job Applications</StatLabel>
          <StatNumber>{metrics.jobApplications.total}</StatNumber>
          <StatHelpText>
            {metrics.jobApplications.byStatus['new'] || 0} new
          </StatHelpText>
        </Stat>

        <Stat p={4} bg="white" borderRadius="lg" boxShadow="sm">
          <StatLabel>Newsletter Subscribers</StatLabel>
          <StatNumber>{metrics.newsletterSubscribers.total}</StatNumber>
          <StatHelpText>
            {metrics.newsletterSubscribers.active} active
          </StatHelpText>
        </Stat>

        <Stat p={4} bg="white" borderRadius="lg" boxShadow="sm">
          <StatLabel>Total Leads</StatLabel>
          <StatNumber>
            {metrics.fleetInquiries.total + 
             metrics.jobApplications.total + 
             metrics.newsletterSubscribers.total}
          </StatNumber>
          <StatHelpText>All CRM entries</StatHelpText>
        </Stat>
      </SimpleGrid>

      {/* Growth Charts */}
      <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={6} mb={6}>
        {/* Fleet Inquiries Trend */}
        <Card>
          <CardHeader>
            <Heading size="sm">Fleet Inquiries (6 Months)</Heading>
          </CardHeader>
          <CardBody>
            {metrics.fleetInquiries.trend.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={metrics.fleetInquiries.trend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" fontSize={11} />
                  <YAxis fontSize={11} />
                  <Tooltip />
                  <Line type="monotone" dataKey="count" stroke="#4299E1" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Text color="gray.500" textAlign="center" py={4}>
                No data available
              </Text>
            )}
          </CardBody>
        </Card>

        {/* Job Applications Trend */}
        <Card>
          <CardHeader>
            <Heading size="sm">Job Applications (6 Months)</Heading>
          </CardHeader>
          <CardBody>
            {metrics.jobApplications.trend.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={metrics.jobApplications.trend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" fontSize={11} />
                  <YAxis fontSize={11} />
                  <Tooltip />
                  <Line type="monotone" dataKey="count" stroke="#805AD5" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Text color="gray.500" textAlign="center" py={4}>
                No data available
              </Text>
            )}
          </CardBody>
        </Card>

        {/* Newsletter Subscribers Trend */}
        <Card>
          <CardHeader>
            <Heading size="sm">Newsletter Growth (6 Months)</Heading>
          </CardHeader>
          <CardBody>
            {metrics.newsletterSubscribers.trend.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={metrics.newsletterSubscribers.trend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" fontSize={11} />
                  <YAxis fontSize={11} />
                  <Tooltip />
                  <Line type="monotone" dataKey="count" stroke="#38A169" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Text color="gray.500" textAlign="center" py={4}>
                No data available
              </Text>
            )}
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Status Distribution */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mb={6}>
        {/* Fleet Inquiries by Status */}
        <Card>
          <CardHeader>
            <Heading size="sm">Fleet Inquiries by Status</Heading>
          </CardHeader>
          <CardBody>
            {Object.keys(metrics.fleetInquiries.byStatus).length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={Object.entries(metrics.fleetInquiries.byStatus).map(([status, count]) => ({
                  status: status.charAt(0).toUpperCase() + status.slice(1),
                  count,
                }))}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="status" fontSize={11} />
                  <YAxis fontSize={11} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#4299E1" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Text color="gray.500" textAlign="center" py={4}>
                No data available
              </Text>
            )}
          </CardBody>
        </Card>

        {/* Job Applications by Status */}
        <Card>
          <CardHeader>
            <Heading size="sm">Job Applications by Status</Heading>
          </CardHeader>
          <CardBody>
            {Object.keys(metrics.jobApplications.byStatus).length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={Object.entries(metrics.jobApplications.byStatus).map(([status, count]) => ({
                  status: status.charAt(0).toUpperCase() + status.slice(1),
                  count,
                }))}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="status" fontSize={11} />
                  <YAxis fontSize={11} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#805AD5" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Text color="gray.500" textAlign="center" py={4}>
                No data available
              </Text>
            )}
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Detailed Tabs */}
      <Tabs lazyBehavior="keepMounted">
        <TabList>
          <Tab>
            <HStack>
              <FiMailIcon />
              <Text>Fleet Inquiries</Text>
            </HStack>
          </Tab>
          <Tab>
            <HStack>
              <FiBriefcaseIcon />
              <Text>Job Applications</Text>
            </HStack>
          </Tab>
          <Tab>
            <HStack>
              <FiUsersIcon />
              <Text>Newsletter</Text>
            </HStack>
          </Tab>
        </TabList>

        <TabPanels>
          <TabPanel p={0} pt={4}>
            <FleetInquiriesTab onDataChange={fetchMetrics} />
          </TabPanel>
          
          <TabPanel p={0} pt={4}>
            <JobApplicationsTab onDataChange={fetchMetrics} />
          </TabPanel>
          
          <TabPanel p={0} pt={4}>
            <NewsletterSubscribersTab onDataChange={fetchMetrics} />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default CRMManagement;
